import * as React from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {DatePicker, DateTimePicker} from '@mui/x-date-pickers';
import {useMutation} from '@tanstack/react-query';
import {createRecurringTasksQuery, createSectionTaskQuery} from '../../services/taskService';
import {useRefreshBoardTasks} from '../../data/tasks';
import {apiErrorMessage} from '../../utilities/apiError';
import {LIMITS} from '../../contract';
import {
    MAX_RECURRING_OCCURRENCES,
    RecurrenceFrequency,
    generateOccurrences,
} from '../../utilities/recurrence';

type Props = {
    boardId: string;
    sections: { id: string, title: string }[];
    defaultSectionId?: string;
    onClose: () => void;
};

type Repeat = 'none' | RecurrenceFrequency;

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const UNIT_LABELS: Record<RecurrenceFrequency, string> = {daily: 'day(s)', weekly: 'week(s)', monthly: 'month(s)'};

// The fuller way to add a task than the per-day quick add: pick the section and
// exact date/time, and optionally repeat it.
export function NewTaskDialog({boardId, sections, defaultSectionId, onClose}: Props) {
    const refreshBoardTasks = useRefreshBoardTasks(boardId);

    const [title, setTitle] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [sectionId, setSectionId] = React.useState(defaultSectionId ?? sections[0]?.id ?? '');
    const [start, setStart] = React.useState<Dayjs | null>(() => dayjs().hour(12).minute(0).second(0).millisecond(0));

    const [repeat, setRepeat] = React.useState<Repeat>('none');
    const [repeatEvery, setRepeatEvery] = React.useState('1');
    const [weekdays, setWeekdays] = React.useState<number[]>(() => [dayjs().day()]);
    const [endType, setEndType] = React.useState<'count' | 'until'>('count');
    const [count, setCount] = React.useState('10');
    const [until, setUntil] = React.useState<Dayjs | null>(null);

    const intervalValue = Number(repeatEvery);
    const countValue = Number(count);

    const occurrences = React.useMemo(() => {
        if (!start || !start.isValid()) return [];
        if (repeat === 'none') return [start];
        if (!Number.isInteger(intervalValue) || intervalValue < 1) return [];

        if (endType === 'count' && (!Number.isInteger(countValue) || countValue < 1)) return [];
        if (endType === 'until' && (!until || !until.isValid())) return [];

        return generateOccurrences(start, {
            frequency: repeat,
            interval: intervalValue,
            weekdays,
            end: endType === 'count' ? {type: 'count', count: countValue} : {type: 'until', until: until!},
        });
    }, [start, repeat, intervalValue, weekdays, endType, countValue, until]);

    const {mutate: createTasks, isPending, error} = useMutation({
        mutationFn: async () => {
            if (repeat === 'none') {
                const taskId = await createSectionTaskQuery(boardId, sectionId, title.trim(), description.trim(), start!.toISOString());
                if (!taskId) throw new Error('Failed to create task');
                return;
            }
            await createRecurringTasksQuery(boardId, sectionId, title.trim(), occurrences.map((o) => o.toISOString()), description.trim());
        },
        onSuccess: () => {
            refreshBoardTasks();
            onClose();
        },
    });

    const canCreate = !!title.trim() && !!sectionId && occurrences.length > 0 && !isPending;
    const errorMessage = error ? apiErrorMessage(error) : undefined;
    const hitCap = repeat !== 'none' && occurrences.length >= MAX_RECURRING_OCCURRENCES;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>New task</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <TextField
                        autoFocus
                        label="Task name"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                    />

                    <TextField
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        minRows={2}
                        maxRows={6}
                        fullWidth
                        slotProps={{htmlInput: {maxLength: LIMITS.description}}}
                        helperText={description ? `${description.length} / ${LIMITS.description}` : 'Where, who with, or what to bring. The spoken summaries use it.'}
                    />

                    <TextField
                        select
                        label="Section"
                        value={sectionId}
                        onChange={(e) => setSectionId(e.target.value)}
                        fullWidth
                    >
                        {sections.map((section) => (
                            <MenuItem key={section.id} value={section.id}>{section.title}</MenuItem>
                        ))}
                    </TextField>

                    <DateTimePicker
                        label={repeat === 'none' ? 'Date' : 'Starts'}
                        value={start}
                        onChange={(value) => setStart(value)}
                    />

                    <TextField
                        select
                        label="Repeat"
                        value={repeat}
                        onChange={(e) => setRepeat(e.target.value as Repeat)}
                        fullWidth
                    >
                        <MenuItem value="none">Does not repeat</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                    </TextField>

                    {repeat !== 'none' && (
                        <>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>Every</Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={repeatEvery}
                                    onChange={(e) => setRepeatEvery(e.target.value)}
                                    slotProps={{htmlInput: {min: 1, 'aria-label': 'Repeat interval'}}}
                                    sx={{width: 80}}
                                />
                                <Typography>{UNIT_LABELS[repeat]}</Typography>
                            </Stack>

                            {repeat === 'weekly' && (
                                <ToggleButtonGroup
                                    size="small"
                                    value={weekdays}
                                    onChange={(_, value: number[]) => setWeekdays(value)}
                                    aria-label="Repeat on"
                                >
                                    {WEEKDAY_LABELS.map((label, day) => (
                                        <ToggleButton key={day} value={day} aria-label={WEEKDAY_NAMES[day]}>
                                            {label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            )}

                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    select
                                    size="small"
                                    label="Ends"
                                    value={endType}
                                    onChange={(e) => setEndType(e.target.value as 'count' | 'until')}
                                    sx={{minWidth: 150}}
                                >
                                    <MenuItem value="count">After</MenuItem>
                                    <MenuItem value="until">On date</MenuItem>
                                </TextField>

                                {endType === 'count' ? (
                                    <>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={count}
                                            onChange={(e) => setCount(e.target.value)}
                                            slotProps={{htmlInput: {min: 1, max: MAX_RECURRING_OCCURRENCES, 'aria-label': 'Number of occurrences'}}}
                                            sx={{width: 90}}
                                        />
                                        <Typography>occurrences</Typography>
                                    </>
                                ) : (
                                    <DatePicker
                                        value={until}
                                        onChange={(value) => setUntil(value)}
                                        slotProps={{textField: {size: 'small'}}}
                                    />
                                )}
                            </Stack>

                            {repeat === 'weekly' && weekdays.length === 0 && (
                                <Alert severity="warning">Pick at least one day of the week.</Alert>
                            )}

                            {occurrences.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    Creates {occurrences.length} task{occurrences.length === 1 ? '' : 's'}:{' '}
                                    {occurrences[0].format('MMM D, YYYY')}
                                    {occurrences.length > 1 && ` – ${occurrences[occurrences.length - 1].format('MMM D, YYYY')}`}
                                    {hitCap && ` (limited to ${MAX_RECURRING_OCCURRENCES})`}
                                </Typography>
                            )}
                        </>
                    )}

                    {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!canCreate} onClick={() => createTasks()}>
                    {repeat === 'none' ? 'Create task' : 'Create tasks'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
